"use client";
import { IconFavorite, IconFavoriteSolid } from "@/app/_components/icons";
import { toast } from "@/app/providers";
import {
  postCreateOrDeleteFavoriteCorp,
  postCreateOrDeleteFavoriteJob,
  postCreateOrDeleteFavoritePersonal,
} from "@/services/api/favorite";
import { FAV_TYPE } from "@/services/constants/global";
import { SIGNIN_PAGE } from "@/services/constants/routerPath";
import { useUserInfo } from "@/services/hooks/useUserInfo";
import { Button, IconButton } from "@chakra-ui/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";

export default function AddToMyFavoriteIconButton({
  id = "", // corporateId, jobId, etc.
  isFavorite = false,
  refetch = () => {},
  favoriteType = FAV_TYPE.CORPORATE,
  buttonSize = 30,
  iconSize = 16,
  label = "",
  ...rest
}) {
  const { t } = useTranslation();
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const user = useUserInfo();
  const [active, setActive] = useState(isFavorite);

  useEffect(() => {
    setActive(isFavorite);
  }, [isFavorite]);

  const mutation = useMutation("favorite", () => {
    switch (favoriteType) {
      case FAV_TYPE.CORPORATE:
        return postCreateOrDeleteFavoriteCorp(id);
      case FAV_TYPE.JOB:
        return postCreateOrDeleteFavoriteJob(id);
      case FAV_TYPE.PERSONAL:
        return postCreateOrDeleteFavoritePersonal(id);
      default:
        return null;
    }
  });
  const onAddToFavorites = (e) => {
    e.stopPropagation();
    if (!user) {
      toast({
        title: "Error",
        description: t("Please login to add to favorites"),
        status: "error",
      });
      const params = new URLSearchParams(searchParams);
      push(`${SIGNIN_PAGE}?redirect=${pathname}?${params}&id=${id}&role=1`);
      return;
    }
    mutation.mutate(id, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: active
            ? t("Removed from favorites successfully")
            : t("Added to favorites successfully"),
          status: "success",
        });
        setActive(!active);
        refetch();
      },
    });
  };

  return label ? (
    <Button
      id={id}
      variant="outline"
      leftIcon={
        active ? (
          <IconFavoriteSolid size={iconSize} color="#232F57" />
        ) : (
          <IconFavorite size={iconSize} color="#232F57" />
        )
      }
      onClick={onAddToFavorites}
      isLoading={mutation.isLoading}
      {...rest}
    >
      {label}
    </Button>
  ) : (
    <IconButton
      id={id}
      rounded="full"
      border={{ lg: "1px solid #E3E3E3" }}
      size={1}
      background="white"
      opacity={0.8}
      h={buttonSize}
      w={buttonSize}
      minW={buttonSize}
      icon={
        active ? (
          <IconFavoriteSolid size={iconSize} color="#232F57" />
        ) : (
          <IconFavorite size={iconSize} color="#232F57" />
        )
      }
      onClick={onAddToFavorites}
      cursor="pointer"
      isLoading={mutation.isLoading}
      {...rest}
    />
  );
}
